
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { apiGetCached, createAxiosHeaders } from '../utils/api';

const getLeaderId = (row) => {
  const candidate = row?.leader ?? row?.leader_id ?? row?.team ?? row?.team_id;
  if (candidate && typeof candidate === 'object') return candidate.id;
  return candidate;
};

const buildRowsFromBulkAverages = (teams, averagesRows) => {
  const grouped = new Map();

  averagesRows.forEach((row) => {
    const leaderId = getLeaderId(row);
    if (leaderId == null) return;

    if (!grouped.has(leaderId)) {
      grouped.set(leaderId, {
        avgSum: 0,
        avgCount: 0,
        judgeCount: 0,
      });
    }

    const record = grouped.get(leaderId);
    record.avgSum += Number(row.average_score || 0);
    record.avgCount += 1;
    record.judgeCount = Math.max(record.judgeCount, Number(row.count || 0));
  });

  const rows = teams.map((team) => {
    const stats = grouped.get(team.id);
    const overall = stats && stats.avgCount > 0 ? stats.avgSum / stats.avgCount : 0;
    return {
      team_id: team.id,
      teamname: team.teamname,
      teampic: team.teampic,
      average_score: parseFloat(overall.toFixed(2)),
      judge_count: stats ? stats.judgeCount : 0,
    };
  });

  rows.sort((a, b) => b.average_score - a.average_score);
  rows.forEach((r, i) => (r.rank = i + 1));
  return rows;
};

const buildRowsWithFallback = async (leagueId, teams, headers) => {
  const rows = await Promise.all(
    teams.map(async (team) => {
      const avgRes = await apiGetCached(
        `/api/team-scores/averages/?league=${leagueId}&leader=${team.id}`,
        headers,
        { ttlMs: 30 * 1000 }
      );
      const perQuestion = avgRes.data || [];
      const sumAvg = perQuestion.reduce((sum, q) => sum + Number(q.average_score || 0), 0);
      const overall = perQuestion.length > 0 ? sumAvg / perQuestion.length : 0;
      const totalJudges =
        perQuestion.reduce((sum, q) => sum + Number(q.count || 0), 0) /
        (perQuestion.length || 1);

      return {
        team_id: team.id,
        teamname: team.teamname,
        teampic: team.teampic,
        average_score: parseFloat(overall.toFixed(2)),
        judge_count: Math.round(totalJudges),
      };
    })
  );

  rows.sort((a, b) => b.average_score - a.average_score);
  rows.forEach((r, i) => (r.rank = i + 1));
  return rows;
};



export default function LeaderboardPanel({ leagueId, leagueName }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState('');

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = createAxiosHeaders();
      const teamsRes = await apiGetCached(
        `/api/teams/?league=${leagueId}`,
        headers,
        { ttlMs: 30 * 1000 }
      );
      const teams = Array.isArray(teamsRes.data) ? teamsRes.data : [];

      let data = [];
      try {
        const bulkRes = await apiGetCached(
          `/api/team-scores/averages/?league=${leagueId}`,
          headers,
          { ttlMs: 30 * 1000 }
        );
        const bulkRows = Array.isArray(bulkRes.data) ? bulkRes.data : [];
        const hasLeaderInfo = bulkRows.some((row) => getLeaderId(row) != null);
        data = hasLeaderInfo
          ? buildRowsFromBulkAverages(teams, bulkRows)
          : await buildRowsWithFallback(leagueId, teams, headers);
      } catch {
        data = await buildRowsWithFallback(leagueId, teams, headers);
      }

      setRows(data);
      setEnabled(true);
    } catch (err) {
      if (err.response?.status === 404) {
        setEnabled(false);
      } else {
        setError('خطا در بارگذاری لیدربورد');
      }
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    if (leagueId) fetchLeaderboard();
  }, [leagueId, fetchLeaderboard]);



  if (loading) {
    return (
      <Box textAlign="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }
  if (!enabled) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6">لیدربورد برای این لیگ فعال نیست.</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }} dir="rtl">
    <h2 className="p-1  text-white w-fit">لیست برترهای لیگ {leagueName}</h2>

<div className="w-full overflow-x-auto bg-gray-900 text-gray-100 border-2 border-slate-300/70 rounded-md mb-4">
  <table className="min-w-full divide-y divide-gray-700">
    <thead className="bg-gray-800">
      <tr>
        <th className="px-4 py-2 text-center text-xs sm:text-sm font-semibold uppercase">رتبه</th>
        <th className="px-4 py-2 text-center text-xs sm:text-sm font-semibold uppercase">عکس</th>
        <th className="px-4 py-2 text-center text-xs sm:text-sm font-semibold uppercase">نام تیم</th>
        <th className="px-4 py-2 text-center text-xs sm:text-sm font-semibold uppercase">میانگین امتیاز کل</th>
        <th className="px-4 py-2 text-center text-xs sm:text-sm font-semibold uppercase">تعداد داوران</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-700">
      {rows.map(r => {
        let rowClasses = "hover:bg-gray-800";
        let textClasses = "text-xs sm:text-sm";
        if (r.rank === 1) {
          rowClasses = "bg-yellow-300/10";
          textClasses = "text-yellow-300";
        } else if (r.rank === 2) {
          rowClasses = "bg-gray-300/10";
          textClasses = "text-gray-300";
        } else if (r.rank === 3) {
          rowClasses = "bg-orange-300/10";
          textClasses = "text-orange-300";
        }
        return (
          <tr key={r.team_id} className={`${rowClasses} text-center`}>
            <td className={`px-4 py-3 ${textClasses}`}>{r.rank}</td>
            <td className={`px-4 py-3 ${textClasses}`}>  
              {r.teampic ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${r.teampic}`}
                  alt={r.teamname}
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full mx-auto"
                />
              ) : (
                <span className={`${textClasses} text-gray-400`}>—</span>
              )}
            </td>
            <td className={`px-4 py-3 ${textClasses}`}>{r.teamname}</td>
            <td className={`px-4 py-3 ${textClasses}`}>{r.average_score}</td>
            <td className={`px-4 py-3 ${textClasses}`}>{r.judge_count}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
</div>

      
    </Box>
  );
}
